import React, { useState, useEffect } from 'react';
import {
    Plus,
    Search,
    Edit3,
    Trash2,
    Filter,
    Loader2,
    Eye
} from 'lucide-react';
import apiService from '../services/api';
import AddEditPujaModal from '../components/modals/AddEditPujaModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { toast } from 'react-hot-toast';

const Pujas = () => {
    const [pujas, setPujas] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('All');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPuja, setEditingPuja] = useState(null);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [pujaToDelete, setPujaToDelete] = useState(null);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [pujasRes, categoriesRes] = await Promise.all([
                apiService.getAllPujas(),
                apiService.getAllPujaCategories()
            ]);

            if (pujasRes.success) setPujas(pujasRes.data);
            else toast.error(pujasRes.message || 'Failed to fetch pujas');

            if (categoriesRes.success) setCategories(categoriesRes.data);
            else toast.error(categoriesRes.message || 'Failed to fetch categories');

        } catch (error) {
            console.error(error);
            toast.error('Error loading data');
        } finally {
            setLoading(false);
        }
    };

    const fetchPujas = async () => {
        try {
            const response = await apiService.getAllPujas();
            if (response.success) setPujas(response.data);
        } catch (error) {
            console.error(error);
        }
    };

    const handleSave = async (data) => {
        try {
            let response;
            if (editingPuja) {
                response = await apiService.updatePuja(editingPuja.id, data);
            } else {
                response = await apiService.createPuja(data);
            }

            if (response.success) {
                toast.success(`Puja ${editingPuja ? 'updated' : 'created'} successfully`);
                fetchPujas();
            } else {
                throw new Error(response.message);
            }
        } catch (error) {
            throw error;
        }
    };

    const handleDelete = async () => {
        if (!pujaToDelete) return;
        try {
            const response = await apiService.deletePuja(pujaToDelete.id);
            if (response.success) {
                toast.success('Puja deleted successfully');
                fetchPujas();
                setIsDeleteModalOpen(false);
            } else {
                toast.error(response.message || 'Failed to delete puja');
            }
        } catch (error) {
            console.error(error);
            toast.error('Error deleting puja');
        }
    };

    const filteredPujas = pujas.filter(puja => {
        const matchesSearch = puja.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = categoryFilter === 'All' || puja.categoryId === categoryFilter;
        return matchesSearch && matchesCategory;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Puja Services</h1>
                    <p className="mt-1 text-sm text-gray-500">Manage individual puja services and details</p>
                </div>
                <button
                    onClick={() => {
                        setEditingPuja(null);
                        setIsModalOpen(true);
                    }}
                    className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Puja
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search pujas..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>
                        <div className="w-full md:w-64">
                            <select
                                value={categoryFilter}
                                onChange={(e) => setCategoryFilter(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            >
                                <option value="All">All Categories</option>
                                {categories.map((cat) => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Puja</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price/Duration</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredPujas.length > 0 ? (
                                filteredPujas.map((puja) => (
                                    <tr key={puja.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="h-10 w-10 flex-shrink-0">
                                                    {puja.image ? (
                                                        <img className="h-10 w-10 rounded-lg object-cover" src={puja.image} alt={puja.name} />
                                                    ) : (
                                                        <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-500 font-bold">
                                                            {puja.name.charAt(0)}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">{puja.name}</div>
                                                    <div className="text-sm text-gray-500">{puja.subtitle}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {puja.category?.name || 'Unknown'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div>₹{puja.price || '0.00'}</div>
                                            <div className="text-xs text-gray-400">{puja.duration}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${puja.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                }`}>
                                                {puja.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => {
                                                    setEditingPuja(puja);
                                                    setIsModalOpen(true);
                                                }}
                                                className="text-indigo-600 hover:text-indigo-900 mr-3"
                                            >
                                                <Edit3 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setPujaToDelete(puja);
                                                    setIsDeleteModalOpen(true);
                                                }}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                        No pujas found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <AddEditPujaModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSave}
                puja={editingPuja}
                categories={categories}
            />

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDelete}
                title="Delete Puja"
                message={`Are you sure you want to delete "${pujaToDelete?.name}"?`}
            />
        </div>
    );
};

export default Pujas;
